import { Component, Input, Output, EventEmitter, forwardRef, ChangeDetectorRef, ViewChild, ElementRef, AfterViewInit, Optional, SkipSelf, Injector, DoCheck } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl, AbstractControl } from '@angular/forms';
import { formatPhoneNumber, getPhoneCursorPosition } from '../../../core/utils/phone-formatter.util';

export interface InputFieldConfig {
  label: string;
  value: string | number;
  placeholder?: string;
  id: string;
  name: string;
  type: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url' | 'search' | 'date' | 'time' | 'datetime-local' | 'month' | 'week' | 'color' | 'range' | 'file' | 'textarea';
  pattern?: string;
  required?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  min?: number | string;
  max?: number | string;
  step?: number;
  maxLength?: number;
  minLength?: number;
  autocomplete?: string;
  rows?: number;
  inputStyle?: { [key: string]: string };
  labelStyle?: { [key: string]: string };
}

@Component({
  selector: 'app-input',
  standalone: false,
  templateUrl: './input.component.html',
  styleUrl: './input.component.css',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true
    }
  ]
})
export class InputComponent implements ControlValueAccessor, AfterViewInit, DoCheck {
  @Input() config?: InputFieldConfig;
  @Input() label?: string;
  @Input() value: string | number = '';
  @Input() placeholder: string = '';
  @Input() id: string = '';
  @Input() name: string = '';
  @Input() type: string = 'text';
  @Input() pattern?: string;
  @Input() required: boolean = false;
  /** Red label + asterisk like a required field, without native HTML5 `required` or implied validation. */
  @Input() highlightLabel: boolean = false;
  @Input() disabled: boolean = false;
  @Input() readonly: boolean = false;
  @Input() min?: number | string;
  @Input() max?: number | string;
  @Input() step?: number;
  @Input() maxLength?: number;
  @Input() minLength?: number;
  @Input() autocomplete?: string;
  @Input() rows: number = 3;
  @Input() inputStyle?: { [key: string]: string };
  @Input() labelStyle?: { [key: string]: string };
  /** When true, placeholder is hidden as soon as input has value. */
  @Input() hidePlaceholderWhenHasValue: boolean = false;
  /** When true, focus selects the full current value for quick overwrite. */
  @Input() selectAllOnFocus: boolean = false;
  @Input() icon: string = '';
  @Input() iconColor: string = 'text-primary';
  /** Tooltip / aria hint for the trailing icon control. */
  @Input() iconTitle: string = '';
  @Input() phoneMask: boolean = false; // Enable phone number formatting

  @Output() valueChange = new EventEmitter<string | number>();
  @Output() blur = new EventEmitter<FocusEvent>();
  @Output() iconClick = new EventEmitter<void>();

  @ViewChild('inputElement', { static: false }) inputElement?: ElementRef<HTMLInputElement>;
  @ViewChild('textareaElement', { static: false }) textareaElement?: ElementRef<HTMLTextAreaElement>;

  private onChange = (value: string | number) => { };
  private onTouched = () => { };
  private innerValue: string | number = '';
  isFocused: boolean = false;
  isTouched: boolean = false;
  private control: AbstractControl | null = null;
  private previousPhoneValue: string = '';
  private previousCursorPosition: number = 0;

  constructor(
    private cdr: ChangeDetectorRef,
    private injector: Injector
  ) {
    // Try to get the form control from the injector
    try {
      const ngControl = this.injector.get(NgControl, null);
      if (ngControl) {
        this.control = ngControl.control;
      }
    } catch (e) {
      // NgControl not available, which is fine
    }
  }

  ngAfterViewInit(): void {
    // Set initial value on native element if it exists
    if (this.innerValue) {
      const nativeElement = this.getNativeElement();
      if (nativeElement) {
        nativeElement.value = String(this.innerValue);
      }
    }
  }

  ngDoCheck(): void {
    // Sync component's touched state with form control's touched state
    if (this.control && this.control.touched && !this.isTouched) {
      this.isTouched = true;
      this.cdr.markForCheck();
    }
  }

  onKeyDown(event: KeyboardEvent): void {
    // Store cursor position for phone formatting BEFORE the key is processed
    if (this.phoneMask || this.displayType === 'tel') {
      const target = event.target as HTMLInputElement;
      if (target) {
        this.previousCursorPosition = target.selectionStart || 0;
        this.previousPhoneValue = target.value;
      }
      
      // For phone inputs, only allow numeric characters and control keys
      const key = event.key;
      const isControlKey =
        event.ctrlKey ||
        event.metaKey ||
        event.altKey ||
        key === 'Backspace' ||
        key === 'Delete' ||
        key === 'Tab' ||
        key === 'Enter' ||
        key === 'ArrowLeft' ||
        key === 'ArrowRight' ||
        key === 'ArrowUp' ||
        key === 'ArrowDown' ||
        key === 'Home' ||
        key === 'End' ||
        (key === 'a' && event.ctrlKey) || // Ctrl+A
        (key === 'c' && event.ctrlKey) || // Ctrl+C
        (key === 'v' && event.ctrlKey) || // Ctrl+V
        (key === 'x' && event.ctrlKey);    // Ctrl+X

      // Allow only digits
      const isDigit = /^\d$/.test(key);

      if (!isControlKey && !isDigit) {
        event.preventDefault();
        return;
      }
    }

    // For number type inputs, only allow numeric characters and control keys
    if (this.displayType === 'number') {
      const key = event.key;
      const isControlKey =
        event.ctrlKey ||
        event.metaKey ||
        event.altKey ||
        key === 'Backspace' ||
        key === 'Delete' ||
        key === 'Tab' ||
        key === 'Enter' ||
        key === 'ArrowLeft' ||
        key === 'ArrowRight' ||
        key === 'ArrowUp' ||
        key === 'ArrowDown' ||
        key === 'Home' ||
        key === 'End' ||
        (key === 'a' && event.ctrlKey) || // Ctrl+A
        (key === 'c' && event.ctrlKey) || // Ctrl+C
        (key === 'v' && event.ctrlKey) || // Ctrl+V
        (key === 'x' && event.ctrlKey);    // Ctrl+X

      // Allow digits, decimal point (only one), and minus sign (only at start)
      const isDigit = /^\d$/.test(key);
      const isDecimal = key === '.' || key === ',';
      const isMinus = key === '-';

      if (!isControlKey && !isDigit && !isDecimal && !isMinus) {
        event.preventDefault();
        return;
      }

      // Additional validation for decimal and minus
      if (isDecimal || isMinus) {
        const target = event.target as HTMLInputElement;
        if (target) {
          const currentValue = target.value;
          const selectionStart = target.selectionStart || 0;

          if (isDecimal && currentValue.includes('.')) {
            // Prevent multiple decimal points
            event.preventDefault();
            return;
          }

          if (isMinus && (selectionStart !== 0 || currentValue.includes('-'))) {
            // Minus only allowed at the start and only once
            event.preventDefault();
            return;
          }
        }
      }
    }
  }

  onKeyUp(event: KeyboardEvent): void {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement;

    // Fallback: If input event didn't fire, manually sync the value
    if (target) {
      const currentValue = target.value;
      const currentInnerValue = String(this.innerValue || '');

      if (currentValue !== currentInnerValue) {
        // Create a synthetic input event to trigger the change handler
        const syntheticEvent = new Event('input', { bubbles: true, cancelable: true });
        Object.defineProperty(syntheticEvent, 'target', { value: target, enumerable: true });
        this.onInputChange(syntheticEvent);
      }
    }
  }

  onPaste(event: ClipboardEvent): void {
    // For phone mask, format pasted content
    if (this.phoneMask || this.displayType === 'tel') {
      event.preventDefault();
      const paste = (event.clipboardData || (window as any).clipboardData).getData('text');
      
      const target = event.target as HTMLInputElement;
      if (target) {
        const start = target.selectionStart || 0;
        const end = target.selectionEnd || 0;
        const currentValue = target.value;
        
        // Extract digits from current value
        const currentDigits = currentValue.replace(/\D/g, '');
        
        // Extract digits from pasted content
        const pastedDigits = paste.replace(/\D/g, '');
        
        // Remove digits that are in the selected range
        const beforeSelectionDigits = currentValue.substring(0, start).replace(/\D/g, '').length;
        const selectedDigitsCount = currentValue.substring(start, end).replace(/\D/g, '').length;
        
        // Combine: digits before selection + pasted digits + digits after selection
        const newDigits = currentDigits.substring(0, beforeSelectionDigits) + 
                         pastedDigits + 
                         currentDigits.substring(beforeSelectionDigits + selectedDigitsCount);
        
        // Limit to 10 digits
        const limitedDigits = newDigits.slice(0, 10);
        
        // Format the result
        const formatted = formatPhoneNumber(limitedDigits);
        target.value = formatted;
        
        // Calculate cursor position after paste
        const digitsAfterPaste = beforeSelectionDigits + pastedDigits.length;
        let digitCount = 0;
        let newCursorPosition = formatted.length;
        
        for (let i = 0; i < formatted.length; i++) {
          if (/\d/.test(formatted[i])) {
            digitCount++;
            if (digitCount === digitsAfterPaste) {
              newCursorPosition = i + 1;
              break;
            }
          }
        }
        
        setTimeout(() => {
          target.setSelectionRange(newCursorPosition, newCursorPosition);
        }, 0);
        
        // Trigger change event
        const syntheticEvent = new Event('input', { bubbles: true, cancelable: true });
        Object.defineProperty(syntheticEvent, 'target', { value: target, enumerable: true });
        this.onInputChange(syntheticEvent);
      }
      return;
    }

    // For number type inputs, filter out non-numeric characters from pasted content
    if (this.displayType === 'number') {
      event.preventDefault();
      const paste = (event.clipboardData || (window as any).clipboardData).getData('text');
      // Remove all non-numeric characters except decimal point and minus sign
      const numericValue = paste.replace(/[^\d.-]/g, '');

      if (numericValue) {
        const target = event.target as HTMLInputElement;
        if (target) {
          const start = target.selectionStart || 0;
          const end = target.selectionEnd || 0;
          const currentValue = target.value;
          const newValue = currentValue.substring(0, start) + numericValue + currentValue.substring(end);

          // Validate the result is a valid number
          if (numericValue === '' || !isNaN(Number(numericValue))) {
            target.value = newValue;
            const syntheticEvent = new Event('input', { bubbles: true, cancelable: true });
            Object.defineProperty(syntheticEvent, 'target', { value: target, enumerable: true });
            this.onInputChange(syntheticEvent);
          }
        }
      }
    }
  }

  onInputChange(event: Event): void {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement;
    if (!target) {
      return;
    }

    let inputValue = target.value;

    // For phone mask, format the phone number
    if (this.phoneMask || this.displayType === 'tel') {
      // Get current cursor position before formatting
      const currentCursorPosition = (target instanceof HTMLInputElement) 
        ? (target.selectionStart || 0) 
        : 0;
      
      // Store original value for cursor calculation
      const originalValue = inputValue;
      
      // Extract ONLY digits from the input value - this preserves the exact order
      // Remove ALL non-digit characters to get pure digit sequence
      const digitsOnly = originalValue.replace(/\D/g, '');
      
      // Limit to exactly 10 digits - no more, no less
      const limitedDigits = digitsOnly.slice(0, 10);
      
      // Format the digits in their original order
      const formatted = formatPhoneNumber(limitedDigits);
      
      // Always update if different (even if just formatting changed)
      if (target.value !== formatted) {
        target.value = formatted;
        inputValue = formatted;
        
        // Calculate new cursor position based on original value
        // Count how many digits appear before the cursor in the original value
        const beforeCursor = originalValue.substring(0, currentCursorPosition);
        const digitsBeforeCursor = beforeCursor.replace(/\D/g, '').length;
        
        // Find position in formatted value where we have the same number of digits
        let digitCount = 0;
        let newCursorPosition = formatted.length;
        
        for (let i = 0; i < formatted.length; i++) {
          if (/\d/.test(formatted[i])) {
            digitCount++;
            if (digitCount === digitsBeforeCursor) {
              // Position cursor after this digit
              newCursorPosition = i + 1;
              break;
            }
          }
        }
        
        // Use setTimeout to ensure the value is set before setting cursor position
        setTimeout(() => {
          if (target instanceof HTMLInputElement) {
            target.setSelectionRange(newCursorPosition, newCursorPosition);
          }
        }, 0);
      }
    }

    // For number type, filter out any non-numeric characters that might have been entered
    if (this.displayType === 'number') {
      // Remove all non-numeric characters except decimal point and minus sign
      inputValue = inputValue.replace(/[^\d.-]/g, '');
      // Ensure only one decimal point
      const parts = inputValue.split('.');
      if (parts.length > 2) {
        inputValue = parts[0] + '.' + parts.slice(1).join('');
      }
      // Ensure minus is only at the start
      if (inputValue.includes('-') && !inputValue.startsWith('-')) {
        inputValue = inputValue.replace(/-/g, '');
        if (inputValue.length > 0) {
          inputValue = '-' + inputValue;
        }
      }
      // Update the input value
      if (target.value !== inputValue) {
        target.value = inputValue;
      }
    }

    let newValue: string | number = inputValue;

    if (this.displayType === 'number') {
      if (inputValue === '' || inputValue === '-') {
        newValue = '';
      } else {
        const parsed = parseFloat(inputValue);
        newValue = isNaN(parsed) ? '' : parsed;
      }
    }

    this.innerValue = newValue;
    this.value = newValue;
    this.onChange(newValue);
    this.valueChange.emit(newValue);
  }

  onCustomChange(value: string | number): void {
    this.innerValue = value;
    this.value = value;
    this.onChange(value);
    this.valueChange.emit(value);
  }

  onInputBlur(event: FocusEvent): void {
    this.isFocused = false;
    this.isTouched = true;
    this.onTouched();
    this.blur.emit(event);
    // Sync touched state with control
    if (this.control) {
      this.control.markAsTouched();
      this.cdr.markForCheck();
    }
  }

  onFocus(event: FocusEvent): void {
    this.isFocused = true;
    this.tryOpenNativePicker(event.target);
    if (this.selectAllOnFocus) {
      const target = event.target as HTMLInputElement | HTMLTextAreaElement | null;
      setTimeout(() => target?.select(), 0);
    }
  }

  onInputClick(event: MouseEvent): void {
    this.tryOpenNativePicker(event.target);
  }

  private tryOpenNativePicker(target: EventTarget | null): void {
    if (this.isDisabled || this.isReadonly) return;
    const type = this.displayType;
    if (type !== 'date' && type !== 'time' && type !== 'datetime-local') return;
    const input = target as HTMLInputElement | null;
    if (!input || typeof input.showPicker !== 'function') return;
    try {
      input.showPicker();
    } catch {
      // Browser may block showPicker in some event contexts; ignore safely.
    }
  }

  onIconClick(): void {
    this.iconClick.emit();
  }

  get hasValue(): boolean {
    const value = String(this.innerValue || this.value || '');
    return value.trim().length > 0;
  }

  get displayLabel(): string {
    return this.config?.label || this.label || '';
  }

  get displayId(): string {
    return this.config?.id || this.id || '';
  }

  get displayName(): string {
    return this.config?.name || this.name || '';
  }

  get displayType(): string {
    return this.config?.type || this.type || 'text';
  }

  get displayPlaceholder(): string {
    return this.config?.placeholder || this.placeholder || '';
  }

  get computedPlaceholder(): string {
    const placeholder = this.displayPlaceholder;
    if (!placeholder) return '';

    if (!this.displayLabel) {
      return this.hidePlaceholderWhenHasValue && this.hasValue ? '' : placeholder;
    }

    const showWithLabel = this.isFocused || (!this.hidePlaceholderWhenHasValue && this.hasValue);
    return showWithLabel ? placeholder : '';
  }

  get displayPattern(): string {
    return this.config?.pattern || this.pattern || '';
  }

  get isRequired(): boolean {
    return this.config?.required ?? this.required;
  }

  /** Red label styling (required and/or {@link highlightLabel}). */
  get isLabelEmphasized(): boolean {
    return this.isRequired || this.highlightLabel;
  }

  get isDisabled(): boolean {
    return this.config?.disabled ?? this.disabled;
  }

  get isReadonly(): boolean {
    return this.config?.readonly ?? this.readonly;
  }

  get displayRows(): number {
    return this.config?.rows ?? this.rows;
  }

  get displayInputStyle(): { [key: string]: string } | undefined {
    return this.config?.inputStyle ?? this.inputStyle;
  }

  /** When parent sets a background via `inputStyle`, omit Tailwind `bg-bg` so inline styles are visible. */
  get hasCustomInputBackground(): boolean {
    const s = this.displayInputStyle;
    if (!s) return false;
    const bg = s['backgroundColor'] ?? s['background'];
    return bg != null && String(bg).trim() !== '';
  }

  get displayLabelStyle(): { [key: string]: string } | undefined {
    return this.config?.labelStyle ?? this.labelStyle;
  }

  writeValue(value: string | number | null | undefined): void {
    if (value === null || value === undefined) {
      this.innerValue = '';
      this.value = '';
    } else {
      // Format phone number if phone mask is enabled
      if (this.phoneMask || this.displayType === 'tel') {
        const formatted = formatPhoneNumber(String(value));
        this.innerValue = formatted;
        this.value = formatted;
      } else {
        this.innerValue = value;
        this.value = value;
      }
    }
    // Update the native element directly
    setTimeout(() => {
      const nativeElement = this.getNativeElement();
      if (nativeElement) {
        let displayValue: string;
        if (this.phoneMask || this.displayType === 'tel') {
          displayValue = formatPhoneNumber(String(this.innerValue ?? ''));
        } else if (this.displayType === 'number' && (this.step === 0.01 || this.config?.step === 0.01)) {
          const num = this.innerValue !== null && this.innerValue !== undefined ? Number(this.innerValue) : NaN;
          displayValue = !isNaN(num) ? num.toFixed(2) : '';
        } else {
          displayValue = this.innerValue !== null && this.innerValue !== undefined ? String(this.innerValue) : '';
        }
        nativeElement.value = displayValue;
      }
      this.cdr.markForCheck();
    }, 0);
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    const nativeElement = this.getNativeElement();
    if (nativeElement) {
      nativeElement.disabled = isDisabled;
    }
  }

  private getNativeElement(): HTMLInputElement | HTMLTextAreaElement | null {
    if (this.displayType === 'textarea') {
      return this.textareaElement?.nativeElement || null;
    } else {
      return this.inputElement?.nativeElement || null;
    }
  }

  get currentValue(): string | number {
    return this.innerValue;
  }

  get hasError(): boolean {
    return !!(this.control && this.control.invalid && (this.control.touched || this.isTouched));
  }
}
